import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getBlogBySlug, getAllBlogs } from '../data/blogData';
import AdSenseUnit from '../components/AdSenseUnit';
import ScrollToTop from '../components/ScrollToTop';
import '../styles/BlogsPage.css';

export default function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getBlogBySlug(slug);
  const allBlogs = getAllBlogs();

  if (!post) {
    return (
      <div className="blogs-page">
        <nav className="navbar navbar-expand-lg blogs-navbar">
          <div className="container">
            <Link className="navbar-brand" to="/">
              <img src="/images/sidebar-logo.png" alt="MeetingPoint Logo" loading="lazy" />
            </Link>
            <Link to="/blogs" className="btn btn-outline-light btn-sm rounded-pill px-3">
              <i className="fas fa-arrow-left me-2"></i>All Blogs
            </Link>
          </div>
        </nav>
        <div className="container text-center py-5">
          <h2>Blog post not found</h2>
          <p className="text-muted">The article you're looking for doesn't exist.</p>
          <Link to="/blogs" className="btn btn-gradient mt-3">Browse All Articles</Link>
        </div>
      </div>
    );
  }

  // Get related posts (same category, excluding current)
  const related = allBlogs.filter(b => b.category === post.category && b.slug !== post.slug).slice(0, 3);

  return (
    <div className="blogs-page">
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg blogs-navbar">
        <div className="container">
          <Link className="navbar-brand" to="/">
            <img src="/images/sidebar-logo.png" alt="MeetingPoint Logo" loading="lazy" />
          </Link>
          <div className="d-flex align-items-center gap-3">
            <Link to="/blogs" className="btn btn-outline-light btn-sm rounded-pill px-3">
              <i className="fas fa-arrow-left me-2"></i>All Blogs
            </Link>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <header className="blog-detail-hero">
        <div className="container">
          <div className="blog-detail-meta-top">
            <span className="blog-detail-category">{post.category}</span>
            <span className="blog-detail-date">
              <i className="far fa-calendar-alt me-1"></i>
              {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="blog-detail-read">
              <i className="far fa-clock me-1"></i>{post.readTime}
            </span>
          </div>
          <h1 className="blog-detail-title">{post.title}</h1>
          <p className="blog-detail-excerpt">{post.excerpt}</p>
        </div>
      </header>

      {/* AdSense Unit - Before Content */}
      <div className="container">
        <AdSenseUnit adSlot="6577495835" adFormat="auto" />
      </div>

      {/* Article Content */}
      <article className="container blog-detail-content">
        <div className="blog-detail-body" dangerouslySetInnerHTML={{ __html: post.content }} />
      </article>

      {/* AdSense Unit - After Content */}
      <div className="container">
        <AdSenseUnit adSlot="2470729863" adFormat="auto" style={{ marginTop: '30px' }} />
      </div>

      {/* Related Posts */}
      {related.length > 0 && (
        <section className="container blog-related-section">
          <h2 className="blog-related-heading">Related Articles</h2>
          <div className="blogs-grid">
            {related.map(rp => (
              <button
                key={rp.slug}
                className="blog-card-link blog-card-btn"
                onClick={() => { navigate(`/blogs/${rp.slug}`); window.scrollTo(0, 0); }}
              >
                <article className="blog-card">
                  <div className="blog-card-icon">
                    <i className={`fas ${rp.icon}`}></i>
                  </div>
                  <span className="blog-card-category">{rp.category}</span>
                  <h3 className="blog-card-title">{rp.title}</h3>
                  <p className="blog-card-excerpt">{rp.excerpt}</p>
                  <div className="blog-card-meta">
                    <span><i className="far fa-clock me-1"></i>{rp.readTime}</span>
                  </div>
                </article>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="blogs-footer">
        <div className="container text-center">
          <p className="mb-1">&copy; {new Date().getFullYear()} MeetingPoint. All rights reserved.</p>
          <div className="d-flex justify-content-center gap-3 mt-2 flex-wrap">
            <Link to="/" className="text-muted">Home</Link>
            <Link to="/about" className="text-muted">About</Link>
            <Link to="/blogs" className="text-muted">Blog</Link>
            <Link to="/faq" className="text-muted">FAQ</Link>
            <Link to="/contact" className="text-muted">Contact</Link>
            <Link to="/privacy" className="text-muted">Privacy Policy</Link>
            <Link to="/terms" className="text-muted">Terms</Link>
            <Link to="/disclaimer" className="text-muted">Disclaimer</Link>
          </div>
        </div>
      </footer>
      <ScrollToTop />
    </div>
  );
}
